import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Globe,
  Image,
  Calendar,
  Utensils,
  MapPin,
  Trophy,
  Flag,
  FileImage,
  Layers,
  FolderOpen,
  Sparkles,
  Mail,
  GraduationCap,
  Newspaper,
  LayoutTemplate,
  Star,
  MessageCircle,
  Megaphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sidebar navigation items
const navItems = [
  { 
    key: 'dashboard', 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    path: '/admin',
    roles: ['admin', 'editor']
  },
  { 
    key: 'home-content', 
    label: 'Contingut HOME', 
    icon: Globe, 
    path: '/admin/home-content',
    roles: ['admin']
  },
  { 
    key: 'carousel', 
    label: 'Carrusel HOME', 
    icon: Image, 
    path: '/admin/carousel',
    roles: ['admin']
  },
  { 
    key: 'sections', 
    label: 'Seccions', 
    icon: Layers, 
    path: '/admin/sections',
    roles: ['admin', 'editor']
  },
  { 
    key: 'pages', 
    label: 'Pàgines', 
    icon: FileText, 
    path: '/admin/pages',
    roles: ['admin', 'editor']
  },
  { 
    key: 'tournaments', 
    label: 'Torneigs', 
    icon: Trophy, 
    path: '/admin/tournaments',
    roles: ['admin', 'editor']
  },
  { 
    key: 'holes', 
    label: 'Forats', 
    icon: Flag, 
    path: '/admin/holes',
    roles: ['admin', 'editor']
  },
  { 
    key: 'documents', 
    label: 'Documents', 
    icon: FolderOpen, 
    path: '/admin/documents',
    roles: ['admin', 'editor']
  },
  { 
    key: 'media', 
    label: 'Imatges i PDF', 
    icon: FileImage, 
    path: '/admin/media',
    roles: ['admin', 'editor']
  },
  { 
    key: 'ctas', 
    label: 'CTAs Flotants', 
    icon: Sparkles, 
    path: '/admin/ctas',
    roles: ['admin']
  },
  { 
    key: 'nuts-program', 
    label: 'Programa NUTS', 
    icon: GraduationCap, 
    path: '/admin/programa-nuts',
    roles: ['admin']
  },
  { 
    key: 'premsa', 
    label: 'Premsa', 
    icon: Newspaper, 
    path: '/admin/premsa',
    roles: ['admin']
  },
  { 
    key: 'iniciacio-anyos', 
    label: 'Iniciació Anyós', 
    icon: Calendar, 
    path: '/admin/iniciacio-anyos',
    roles: ['admin']
  },
  { 
    key: 'minisites', 
    label: 'Minisites', 
    icon: LayoutTemplate, 
    path: '/admin/minisites',
    roles: ['admin']
  },
  { 
    key: 'soldeu-landing', 
    label: 'Landing Soldeu', 
    icon: LayoutTemplate, 
    path: '/admin/soldeu-landing',
    roles: ['admin']
  },
  { 
    key: 'sig-landing', 
    label: 'Landing Curs Iniciació', 
    icon: LayoutTemplate, 
    path: '/admin/sig-landing',
    roles: ['admin']
  },
  { 
    key: 'ticker', 
    label: 'Avís al peu (Ticker)', 
    icon: Megaphone, 
    path: '/admin/ticker',
    roles: ['admin', 'editor']
  },
  { 
    key: 'enquestes', 
    label: 'Enquestes', 
    icon: Star, 
    path: '/admin/enquestes',
    roles: ['admin', 'editor']
  },
  { 
    key: 'whatsapp-autoreplies', 
    label: 'Bot WhatsApp', 
    icon: MessageCircle, 
    path: '/admin/whatsapp-autoreplies',
    roles: ['admin']
  },
  { 
    key: 'contacts', 
    label: 'Missatges', 
    icon: Mail, 
    path: '/admin/contacts',
    roles: ['admin']
  },
  { 
    key: 'users', 
    label: 'Usuaris', 
    icon: Users, 
    path: '/admin/users',
    roles: ['admin']
  },
  { 
    key: 'config', 
    label: 'Configuració', 
    icon: Settings, 
    path: '/admin/config',
    roles: ['admin']
  },
];

// Page sections for editing
const pageItems = [
  { key: 'home', label: 'Inici', icon: LayoutDashboard },
  { key: 'el-camp', label: 'El Camp', icon: MapPin },
  { key: 'jugar', label: 'Jugar', icon: Globe },
  { key: 'competicio', label: 'Competició', icon: Trophy },
  { key: 'iniciacio', label: 'Acadèmia', icon: Users },
  { key: 'driving-range', label: 'Pràctiques', icon: Flag },
  { key: 'restaurant', label: 'Restaurant', icon: Utensils },
  { key: 'allotjament', label: 'Allotjament', icon: Image },
  { key: 'correspondencies', label: 'Correspondències', icon: Globe },
  { key: 'contacte', label: 'Contacte', icon: MapPin },
  { key: 'equip', label: "L'Equip", icon: Users },
];

export const AdminLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role || 'editor')
  );

  return (
    <div className="min-h-screen bg-[#F5F5F3]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E5E5E5] z-50 flex items-center justify-between px-4">
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-[#F5F5F5] rounded"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <img 
          src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
          alt="UGOLF Aravell"
          className="h-8 brightness-0"
        />
        <div className="w-10" />
      </div>

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full bg-white border-r border-[#E5E5E5] z-40
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-64' : 'w-20'}
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-center border-b border-[#E5E5E5]">
          <img 
            src="https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/ibb3s625_UGOLF%20ARAVELL%20-%20ANDORRA%20-%20BLANCO.svg"
            alt="UGOLF Aravell"
            className={`brightness-0 transition-all ${sidebarOpen ? 'h-10' : 'h-8'}`}
          />
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
          {filteredNavItems.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg
                text-[#666666] hover:bg-[#F5F5F5] hover:text-[#1A4D2E]
                transition-colors duration-200
                ${!sidebarOpen && 'justify-center'}
              `}
              data-testid={`nav-${item.key}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User info & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E5E5E5] bg-white">
          {sidebarOpen ? (
            <div className="space-y-3">
              {/* Guide link */}
              <a
                href="/api/static/guia-imatges-editors.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xs text-[#1A4D2E] hover:underline"
              >
                <FileImage className="w-3.5 h-3.5" />
                Guia d'imatges per editors
              </a>
              
              <div className="flex items-center gap-3">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white font-medium">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#333333] truncate">{user?.name}</p>
                  <p className="text-xs text-[#888888] capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="w-full justify-start gap-2 text-[#666666]"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Tancar sessió
              </Button>
            </div>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full flex justify-center p-2 text-[#666666] hover:text-[#1A4D2E]"
              title="Tancar sessió"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Toggle sidebar */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hidden lg:flex absolute top-20 -right-3 w-6 h-6 bg-white border border-[#E5E5E5] rounded-full items-center justify-center shadow-sm hover:bg-[#F5F5F5]"
        >
          <ChevronRight className={`w-4 h-4 transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main 
        className={`
          transition-all duration-300
          pt-16 lg:pt-0
          ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        `}
      >
        <div className="p-6 lg:p-8 pb-48 min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
